import { Injectable } from '@angular/core';
import { AuthenticationService } from './auth.service';
import { TrialInfo } from './access-state.service';

/**
 * Aviso de fin del periodo beta (el pop-up que se muestra al entrar al
 * dashboard).
 *
 * Los 7 días son ahora responsabilidad del backend: `trial.started_at` es
 * inmutable por empresa (arranca en el primer login, no se reinicia si se
 * añade un usuario a mitad de prueba) y `trial.ends_at` refleja prórrogas
 * concedidas por un admin. Este servicio solo lee ese estado vía
 * `authService.trial$` — ya no calcula ni guarda ninguna fecha por su cuenta.
 *
 * Se muestra a todos los usuarios con prueba iniciada, sin excepción por
 * dominio de correo.
 *
 * Si el plazo ya pasó, el aviso igual se muestra, informando de que no quedan
 * días — coexiste con el banner del dashboard y con el bloqueo del chat, que
 * leen el mismo `trial$`.
 */
@Injectable({ providedIn: 'root' })
export class BetaNoticeService {
  private static readonly DISMISS_KEY = 'beta_notice_dismissed';

  private trialInfo: TrialInfo | null = null;

  constructor(private authService: AuthenticationService) {
    this.authService.trial$.subscribe((trial) => {
      this.trialInfo = trial;
    });
  }

  /** Días completos que faltan hasta el fin del plazo. 0 si ya pasó. */
  get daysRemaining(): number {
    const days = Number(this.trialInfo?.days_remaining);
    return Number.isFinite(days) && days > 0 ? days : 0;
  }

  /** El plazo de esta empresa ya pasó. */
  get expired(): boolean {
    return !!this.trialInfo?.expired;
  }

  /** El aviso debe mostrarse: hay prueba iniciada y no se cerró en esta sesión. */
  get visible(): boolean {
    return !!this.trialInfo?.started && !this.wasDismissed();
  }

  /** Cierra el aviso y no vuelve a mostrarlo en esta sesión del navegador. */
  dismiss(): void {
    try {
      sessionStorage.setItem(BetaNoticeService.DISMISS_KEY, '1');
    } catch (e) {
      // safe: si el storage no está disponible, solo se oculta en memoria
    }
  }

  private wasDismissed(): boolean {
    try {
      return sessionStorage.getItem(BetaNoticeService.DISMISS_KEY) === '1';
    } catch (e) {
      return false;
    }
  }
}
